import React, { useState, useEffect, useCallback, useRef } from 'react';
import './profile.css';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { apiFetch } from '../../api/client';
import { FiEdit2, FiPlus, FiTrash2, FiX, FiCheck, FiMapPin, FiMail, FiPhone, FiUpload } from 'react-icons/fi';

const emptyExperience = { title: '', company: '', location: '', start: '', end: '', bullets: [''] };
const emptyEducation = { school: '', degree: '', field: '', start: '', end: '', details: '' };
const emptyProject = { name: '', bullets: [''] };
const emptyCert = { name: '', issuer: '', date: '' };
const emptyPublication = { title: '', authors: '', venue: '', date: '', url: '', details: '' };

function SectionCard({ title, editing, onEdit, onSave, onCancel, saving, children, accent }) {
  return (
    <div className={`profileCard ${accent ? `accent-${accent}` : ''}`}>
      <div className="profileCardHeader">
        <h2 className="profileCardTitle">{title}</h2>
        {!editing ? (
          <button className="pillBtn ghost small" onClick={onEdit}>
            <FiEdit2 size={13} /> Edit
          </button>
        ) : (
          <div className="cardHeaderActions">
            <button className="pillBtn ghost small" onClick={onCancel}><FiX size={13} /> Cancel</button>
            <button className="pillBtn solid small" onClick={onSave} disabled={saving}>
              <FiCheck size={13} /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function Profile() {
  const { darkMode } = useTheme();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [missing, setMissing] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileInputRef = useRef(null);

  const redirectedIncomplete = location.state && location.state.incomplete;

  const loadProfile = useCallback(async () => {
    try {
      const data = await apiFetch('/api/profile');
      setProfile(data.profile);
      setIsComplete(data.isComplete);
      setMissing(data.missing || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const startEdit = (section) => {
    setEditingSection(section);
    setDraft(JSON.parse(JSON.stringify(profile)));
    setSkillInput('');
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setDraft(null);
  };

  const saveSection = async (fields) => {
    setSaving(true);
    setError(null);
    try {
      const body = {};
      fields.forEach((f) => { body[f] = draft[f]; });
      const data = await apiFetch('/api/profile', { method: 'PUT', body });
      setProfile(data.profile);
      setIsComplete(data.isComplete);
      setMissing(data.missing || []);
      setEditingSection(null);
      setDraft(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setDraftField = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const setLinkField = (key, value) =>
    setDraft((d) => ({ ...d, links: { ...(d.links || {}), [key]: value } }));

  const updateArrayItem = (field, idx, key, value) => {
    setDraft((d) => {
      const arr = [...(d[field] || [])];
      arr[idx] = { ...arr[idx], [key]: value };
      return { ...d, [field]: arr };
    });
  };

  const addArrayItem = (field, empty) => {
    setDraft((d) => ({
      ...d,
      [field]: [...(d[field] || []), JSON.parse(JSON.stringify(empty))]
    }));
  };

  const removeArrayItem = (field, idx) => {
    setDraft((d) => ({ ...d, [field]: (d[field] || []).filter((_, i) => i !== idx) }));
  };

  const addSkill = () => {
    const s = skillInput.trim();
    if (!s) return;
    setDraft((d) => (d.skills.includes(s) ? d : { ...d, skills: [...d.skills, s] }));
    setSkillInput('');
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setUploadMsg({ type: 'error', text: 'Only PDF files are supported.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadMsg({ type: 'error', text: 'File too large (max 5 MB).' });
      return;
    }

    setUploading(true);
    setUploadMsg(null);
    try {
      const fd = new FormData();
      fd.append('resume', file);

      const data = await apiFetch('/api/profile/upload-resume', {
        method: 'POST',
        formData: fd
      });

      setProfile(data.profile);
      setIsComplete(data.isComplete);
      setMissing(data.missing || []);
      const count = (data.parsedFields || []).length;
      setUploadMsg({ type: 'success', text: `Imported ${count} section${count !== 1 ? 's' : ''} from your resume.` });
    } catch (err) {
      setUploadMsg({ type: 'error', text: err.message || 'Failed to parse resume.' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className={`profileContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="profileLoading">Loading profile...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`profileContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="profileLoading">{error || 'Could not load profile.'}</div>
      </div>
    );
  }

  const initials = (profile.fullName || profile.email || '?')
    .split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

  const data = editingSection ? draft : profile;

  return (
    <div className={`profileContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="profileInner">

        {(!isComplete) && (
          <div className={`completeBanner ${redirectedIncomplete ? 'highlight' : ''}`}>
            <div className="completeBannerTitle">Complete your profile to start building resumes</div>
            <ul className="completeChecklist">
              {missing.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        )}

        {error && <div className="profileError">{error}</div>}

        {/* ===== Header ===== */}
        <div className="profileHeader">
          <div className="profileAvatar">{initials}</div>
          <h1 className="profileName">{profile.fullName || 'Your Name'}</h1>
          <p className="profileHeadline">{profile.headline || 'Add a headline to describe yourself'}</p>

          <div className="profileStats">
            <div className="statBlock">
              <span className="statValue">{profile.experiences.length}</span>
              <span className="statLabel">Experiences</span>
            </div>
            <div className="statBlock">
              <span className="statValue">{profile.skills.length}</span>
              <span className="statLabel">Skills</span>
            </div>
            <div className="statBlock">
              <span className="statValue">{isComplete ? '100%' : `${Math.round(((4 - missing.length) / 4) * 100)}%`}</span>
              <span className="statLabel">Complete</span>
            </div>
          </div>

          <div className="profileMeta">
            {profile.location && <span className="metaItem"><FiMapPin size={13} /> {profile.location}</span>}
            {profile.email && <span className="metaItem"><FiMail size={13} /> {profile.email}</span>}
            {profile.phone && <span className="metaItem"><FiPhone size={13} /> {profile.phone}</span>}
          </div>

          {profile.skills.length > 0 && (
            <div className="profileTagRow">
              {profile.skills.slice(0, 6).map((s) => <span key={s} className="tagChip">{s}</span>)}
            </div>
          )}

          <div className="resumeUploadSection">
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleResumeUpload}
              className="hiddenFileInput"
              disabled={uploading}
            />
            <button
              className="pillBtn ghost resumeUploadBtn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <FiUpload size={14} />
              {uploading ? 'Parsing resume…' : 'Import from PDF'}
            </button>
            <span className="uploadHint">Upload an existing resume to auto-fill your profile</span>
            {uploadMsg && (
              <div className={`uploadMsg ${uploadMsg.type}`}>{uploadMsg.text}</div>
            )}
          </div>
        </div>

        {/* ===== Personal Info ===== */}
        <SectionCard
          title="Personal Info"
          editing={editingSection === 'personal'}
          onEdit={() => startEdit('personal')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['fullName', 'headline', 'location', 'phone', 'email', 'links'])}
          saving={saving}
        >
          {editingSection === 'personal' ? (
            <div className="formGrid">
              <label>Full name<input value={data.fullName} onChange={(e) => setDraftField('fullName', e.target.value)} placeholder="Jane Doe" /></label>
              <label>Headline<input value={data.headline} onChange={(e) => setDraftField('headline', e.target.value)} placeholder="Software Engineer" /></label>
              <label>Location<input value={data.location} onChange={(e) => setDraftField('location', e.target.value)} placeholder="City, Country" /></label>
              <label>Phone<input value={data.phone} onChange={(e) => setDraftField('phone', e.target.value)} placeholder="+1 555 000 0000" /></label>
              <label>Email<input value={data.email} onChange={(e) => setDraftField('email', e.target.value)} placeholder="jane@email.com" /></label>
              <label>GitHub<input value={(data.links || {}).github || ''} onChange={(e) => setLinkField('github', e.target.value)} placeholder="github.com/username" /></label>
              <label>LinkedIn<input value={(data.links || {}).linkedin || ''} onChange={(e) => setLinkField('linkedin', e.target.value)} placeholder="linkedin.com/in/username" /></label>
              <label>Portfolio<input value={(data.links || {}).portfolio || ''} onChange={(e) => setLinkField('portfolio', e.target.value)} placeholder="yoursite.com" /></label>
            </div>
          ) : (
            <div className="infoGrid">
              <div><span className="infoLabel">Full name</span><span className="infoValue">{profile.fullName || '—'}</span></div>
              <div><span className="infoLabel">Headline</span><span className="infoValue">{profile.headline || '—'}</span></div>
              <div><span className="infoLabel">Location</span><span className="infoValue">{profile.location || '—'}</span></div>
              <div><span className="infoLabel">Phone</span><span className="infoValue">{profile.phone || '—'}</span></div>
              <div><span className="infoLabel">Email</span><span className="infoValue">{profile.email || '—'}</span></div>
              <div><span className="infoLabel">GitHub</span><span className="infoValue">{(profile.links || {}).github || '—'}</span></div>
              <div><span className="infoLabel">LinkedIn</span><span className="infoValue">{(profile.links || {}).linkedin || '—'}</span></div>
              <div><span className="infoLabel">Portfolio</span><span className="infoValue">{(profile.links || {}).portfolio || '—'}</span></div>
            </div>
          )}
        </SectionCard>

        {/* ===== Summary ===== */}
        <SectionCard
          title="Summary"
          editing={editingSection === 'summary'}
          onEdit={() => startEdit('summary')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['summary'])}
          saving={saving}
        >
          {editingSection === 'summary' ? (
            <textarea
              className="summaryTextarea"
              rows={5}
              value={data.summary}
              onChange={(e) => setDraftField('summary', e.target.value)}
              placeholder="A short professional summary about yourself..."
            />
          ) : (
            <p className="summaryText">{profile.summary || 'No summary yet.'}</p>
          )}
        </SectionCard>

        {/* ===== Work Experience ===== */}
        <SectionCard
          title="Work Experience"
          editing={editingSection === 'experiences'}
          onEdit={() => startEdit('experiences')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['experiences'])}
          saving={saving}
        >
          {editingSection === 'experiences' ? (
            <div className="entryEditList">
              {data.experiences.map((exp, i) => (
                <div className="entryEdit" key={i}>
                  <button className="entryRemove" onClick={() => removeArrayItem('experiences', i)} title="Remove"><FiTrash2 size={14} /></button>
                  <div className="formGrid">
                    <label>Title<input value={exp.title} onChange={(e) => updateArrayItem('experiences', i, 'title', e.target.value)} placeholder="Software Engineer" /></label>
                    <label>Company<input value={exp.company} onChange={(e) => updateArrayItem('experiences', i, 'company', e.target.value)} placeholder="Acme Inc." /></label>
                    <label>Location<input value={exp.location} onChange={(e) => updateArrayItem('experiences', i, 'location', e.target.value)} placeholder="Remote" /></label>
                    <label>Start<input value={exp.start} onChange={(e) => updateArrayItem('experiences', i, 'start', e.target.value)} placeholder="Jun 2022" /></label>
                    <label>End<input value={exp.end} onChange={(e) => updateArrayItem('experiences', i, 'end', e.target.value)} placeholder="Present" /></label>
                  </div>
                  <div className="bulletsEdit">
                    <span className="infoLabel">Bullets</span>
                    {exp.bullets.map((b, bi) => (
                      <div className="bulletRow" key={bi}>
                        <input
                          value={b}
                          onChange={(e) => {
                            const bullets = [...exp.bullets];
                            bullets[bi] = e.target.value;
                            updateArrayItem('experiences', i, 'bullets', bullets);
                          }}
                          placeholder="Achievement or responsibility..."
                        />
                        <button onClick={() => updateArrayItem('experiences', i, 'bullets', exp.bullets.filter((_, x) => x !== bi))}><FiX size={13} /></button>
                      </div>
                    ))}
                    <button className="pillBtn ghost small" onClick={() => updateArrayItem('experiences', i, 'bullets', [...exp.bullets, ''])}>
                      <FiPlus size={13} /> Add bullet
                    </button>
                  </div>
                </div>
              ))}
              <button className="pillBtn ghost" onClick={() => addArrayItem('experiences', emptyExperience)}><FiPlus size={14} /> Add experience</button>
            </div>
          ) : profile.experiences.length === 0 ? (
            <p className="emptyText">No work experience added yet.</p>
          ) : (
            <div className="entryList">
              {profile.experiences.map((exp, i) => (
                <div className="entry" key={i}>
                  <div className="entryHead">
                    <span className="entryTitle">{exp.title}{exp.company ? ` · ${exp.company}` : ''}</span>
                    <span className="entryDates">{[exp.start, exp.end].filter(Boolean).join(' — ')}</span>
                  </div>
                  {exp.location && <span className="entrySub">{exp.location}</span>}
                  {exp.bullets.filter(Boolean).length > 0 && (
                    <ul className="entryBullets">{exp.bullets.filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ===== Education ===== */}
        <SectionCard
          title="Education"
          editing={editingSection === 'education'}
          onEdit={() => startEdit('education')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['education'])}
          saving={saving}
        >
          {editingSection === 'education' ? (
            <div className="entryEditList">
              {data.education.map((ed, i) => (
                <div className="entryEdit" key={i}>
                  <button className="entryRemove" onClick={() => removeArrayItem('education', i)} title="Remove"><FiTrash2 size={14} /></button>
                  <div className="formGrid">
                    <label>School<input value={ed.school} onChange={(e) => updateArrayItem('education', i, 'school', e.target.value)} placeholder="University of..." /></label>
                    <label>Degree<input value={ed.degree} onChange={(e) => updateArrayItem('education', i, 'degree', e.target.value)} placeholder="B.S." /></label>
                    <label>Field<input value={ed.field} onChange={(e) => updateArrayItem('education', i, 'field', e.target.value)} placeholder="Computer Science" /></label>
                    <label>Start<input value={ed.start} onChange={(e) => updateArrayItem('education', i, 'start', e.target.value)} placeholder="2019" /></label>
                    <label>End<input value={ed.end} onChange={(e) => updateArrayItem('education', i, 'end', e.target.value)} placeholder="2023" /></label>
                    <label>Details<input value={ed.details} onChange={(e) => updateArrayItem('education', i, 'details', e.target.value)} placeholder="GPA, honors, coursework..." /></label>
                  </div>
                </div>
              ))}
              <button className="pillBtn ghost" onClick={() => addArrayItem('education', emptyEducation)}><FiPlus size={14} /> Add education</button>
            </div>
          ) : profile.education.length === 0 ? (
            <p className="emptyText">No education added yet.</p>
          ) : (
            <div className="entryList">
              {profile.education.map((ed, i) => (
                <div className="entry" key={i}>
                  <div className="entryHead">
                    <span className="entryTitle">{[ed.degree, ed.field].filter(Boolean).join(' in ')}{ed.school ? ` · ${ed.school}` : ''}</span>
                    <span className="entryDates">{[ed.start, ed.end].filter(Boolean).join(' — ')}</span>
                  </div>
                  {ed.details && <span className="entrySub">{ed.details}</span>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ===== Skills ===== */}
        <SectionCard
          title="Skills"
          editing={editingSection === 'skills'}
          onEdit={() => startEdit('skills')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['skills'])}
          saving={saving}
          accent="green"
        >
          {editingSection === 'skills' ? (
            <div>
              <div className="skillInputRow">
                <input
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
                  placeholder="Type a skill and press Enter"
                />
                <button className="pillBtn solid small" onClick={addSkill}><FiPlus size={13} /> Add</button>
              </div>
              <div className="profileTagRow editTags">
                {data.skills.map((s) => (
                  <span key={s} className="tagChip removable" onClick={() => setDraftField('skills', data.skills.filter((x) => x !== s))}>
                    {s} <FiX size={11} />
                  </span>
                ))}
              </div>
            </div>
          ) : profile.skills.length === 0 ? (
            <p className="emptyText">No skills added yet. Add at least 3.</p>
          ) : (
            <div className="profileTagRow">
              {profile.skills.map((s) => <span key={s} className="tagChip">{s}</span>)}
            </div>
          )}
        </SectionCard>

        {/* ===== Projects ===== */}
        <SectionCard
          title="Projects"
          editing={editingSection === 'projects'}
          onEdit={() => startEdit('projects')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['projects'])}
          saving={saving}
        >
          {editingSection === 'projects' ? (
            <div className="entryEditList">
              {data.projects.map((p, i) => (
                <div className="entryEdit" key={i}>
                  <button className="entryRemove" onClick={() => removeArrayItem('projects', i)} title="Remove"><FiTrash2 size={14} /></button>
                  <div className="formGrid">
                    <label className="fullWidth">Name<input value={p.name} onChange={(e) => updateArrayItem('projects', i, 'name', e.target.value)} placeholder="Project name" /></label>
                  </div>
                  <div className="bulletsEdit">
                    <span className="bulletsLabel">Bullet points</span>
                    {(p.bullets?.length ? p.bullets : ['']).map((b, bi) => (
                      <div className="bulletRow" key={bi}>
                        <input
                          value={b}
                          onChange={(e) => {
                            const bullets = [...(p.bullets?.length ? p.bullets : [''])];
                            bullets[bi] = e.target.value;
                            updateArrayItem('projects', i, 'bullets', bullets);
                          }}
                          placeholder="What you built, tech used, outcome..."
                        />
                        <button type="button" onClick={() => updateArrayItem('projects', i, 'bullets', (p.bullets || ['']).filter((_, x) => x !== bi))}><FiX size={13} /></button>
                      </div>
                    ))}
                    <button type="button" className="pillBtn ghost small" onClick={() => updateArrayItem('projects', i, 'bullets', [...(p.bullets?.length ? p.bullets : ['']), ''])}>
                      <FiPlus size={13} /> Add bullet
                    </button>
                  </div>
                </div>
              ))}
              <button className="pillBtn ghost" onClick={() => addArrayItem('projects', emptyProject)}><FiPlus size={14} /> Add project</button>
            </div>
          ) : profile.projects.length === 0 ? (
            <p className="emptyText">No projects added yet.</p>
          ) : (
            <div className="entryList">
              {profile.projects.map((p, i) => (
                <div className="entry" key={i}>
                  <div className="entryHead">
                    <span className="entryTitle">{p.name}</span>
                  </div>
                  {(p.bullets || []).filter(Boolean).length > 0 ? (
                    <ul className="entryBullets">{(p.bullets || []).filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
                  ) : (
                    <span className="entrySub">No bullet points yet.</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ===== Publications ===== */}
        <SectionCard
          title="Publications"
          editing={editingSection === 'publications'}
          onEdit={() => startEdit('publications')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['publications'])}
          saving={saving}
        >
          {editingSection === 'publications' ? (
            <div className="entryEditList">
              {(data.publications || []).map((p, i) => (
                <div className="entryEdit" key={i}>
                  <button className="entryRemove" onClick={() => removeArrayItem('publications', i)} title="Remove"><FiTrash2 size={14} /></button>
                  <div className="formGrid">
                    <label className="fullWidth">Title<input value={p.title} onChange={(e) => updateArrayItem('publications', i, 'title', e.target.value)} placeholder="Paper or article title" /></label>
                    <label className="fullWidth">Authors<input value={p.authors} onChange={(e) => updateArrayItem('publications', i, 'authors', e.target.value)} placeholder="John Doe, Co-Author 1, Co-Author 2" /></label>
                    <label>Venue<input value={p.venue} onChange={(e) => updateArrayItem('publications', i, 'venue', e.target.value)} placeholder="Conference or journal" /></label>
                    <label>Date<input value={p.date} onChange={(e) => updateArrayItem('publications', i, 'date', e.target.value)} placeholder="2024" /></label>
                    <label>URL<input value={p.url} onChange={(e) => updateArrayItem('publications', i, 'url', e.target.value)} placeholder="https://arxiv.org/..." /></label>
                    <label>Details<input value={p.details} onChange={(e) => updateArrayItem('publications', i, 'details', e.target.value)} placeholder="Under review, Best Paper Award, etc." /></label>
                  </div>
                </div>
              ))}
              <button className="pillBtn ghost" onClick={() => addArrayItem('publications', emptyPublication)}><FiPlus size={14} /> Add publication</button>
            </div>
          ) : !(profile.publications || []).length ? (
            <p className="emptyText">No publications added yet.</p>
          ) : (
            <div className="entryList">
              {(profile.publications || []).map((p, i) => (
                <div className="entry" key={i}>
                  <div className="entryHead">
                    <span className="entryTitle">{p.title}</span>
                    <span className="entryDates">{p.date}</span>
                  </div>
                  {p.authors && <span className="entrySub">{p.authors}</span>}
                  {(p.venue || p.details) && (
                    <span className="entrySub">{[p.venue, p.details].filter(Boolean).join(' · ')}</span>
                  )}
                  {p.url && <span className="entrySub">{p.url}</span>}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* ===== Certifications ===== */}
        <SectionCard
          title="Certifications"
          editing={editingSection === 'certs'}
          onEdit={() => startEdit('certs')}
          onCancel={cancelEdit}
          onSave={() => saveSection(['certs'])}
          saving={saving}
        >
          {editingSection === 'certs' ? (
            <div className="entryEditList">
              {data.certs.map((c, i) => (
                <div className="entryEdit" key={i}>
                  <button className="entryRemove" onClick={() => removeArrayItem('certs', i)} title="Remove"><FiTrash2 size={14} /></button>
                  <div className="formGrid">
                    <label>Name<input value={c.name} onChange={(e) => updateArrayItem('certs', i, 'name', e.target.value)} placeholder="AWS Solutions Architect" /></label>
                    <label>Issuer<input value={c.issuer} onChange={(e) => updateArrayItem('certs', i, 'issuer', e.target.value)} placeholder="Amazon" /></label>
                    <label>Date<input value={c.date} onChange={(e) => updateArrayItem('certs', i, 'date', e.target.value)} placeholder="2024" /></label>
                  </div>
                </div>
              ))}
              <button className="pillBtn ghost" onClick={() => addArrayItem('certs', emptyCert)}><FiPlus size={14} /> Add certification</button>
            </div>
          ) : profile.certs.length === 0 ? (
            <p className="emptyText">No certifications added yet.</p>
          ) : (
            <div className="entryList">
              {profile.certs.map((c, i) => (
                <div className="entry" key={i}>
                  <div className="entryHead">
                    <span className="entryTitle">{c.name}{c.issuer ? ` · ${c.issuer}` : ''}</span>
                    <span className="entryDates">{c.date}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>

      </div>
    </div>
  );
}

export default Profile;
